import React, {createRef, useState} from "react";
import {appSelector} from "../../redux/app/selectors";
import {useDispatch} from "react-redux";
import ReCAPTCHA from "react-google-recaptcha";
// material-ui components
// @material-ui/icons
import {
  TextField,
  Button,
  Checkbox,
  CircularProgress,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Grid,
  Typography
} from "@mui/material";
import styled from "styled-components";
import PhoneIcon from "../icons/PhoneIcon";
import {handleSubmit} from "../../lib/utils";
import {validateInput} from "../../lib/validation";
import {media} from "../../../styles/styles";
import {useRouter} from 'next/router'
import {Event} from '../../../utils/gtm';
import {FbEvent} from '../../../utils/fbq';

// core components
const Section = styled.div `
  overflow: hidden;
    padding: 0;
    margin: 0 -1rem;
    p, span, a {
      font-size: 1.1rem;
      line-height: 1.4;
    }
  `

const StyledPhoneIcon = styled(PhoneIcon)`
  position: relative !important;
  left: 0px !important;
  top: 0.5em !important;

  
  @media(min-width: 850px) {
    width: 3rem !important;
    height: 3rem !important;
    position: absolute !important;
    left: -3rem !important;
    top: 75% !important;
    transform: translateY(-50%);
  }
  
  path {
    stroke: #de4a22;
  }
`;
const PhoneNumber = styled.div`
  font-weight: bold;
  width: fit-content;
  margin: auto 0 auto auto;
  position: relative;
  
  .phone_number {
    @media(min-width: 850px) { font-size: 1.6rem; }
    &:hover {
      color:#E55934; 
      text-decoration: underline;
    }
  }
 
`;
const StyledButton = styled(Button)`
  && {
    background-color: #de4a22;
    color: #fff;
    padding: 0.8em 4em;
    ${media.phone`
    width: 100%;`}
    &:hover {
      background-color: #fff;
      color: #de4a22;
    }
  }
`;

const Form = styled.form`
  & > .MuiFormControl-root {
    &:nth-of-type(odd) {
      margin: 2% 3% 2% 0;
    }
    &:nth-of-type(even) {
      margin: 2% 0 2% 3%;
    }
    width: 47%;
    ${media.phone`
    width: 100%;
    &:nth-of-type(odd),&:nth-of-type(even) {
      margin: 2% 0 2% 0;
    }
  `};
  }
`;
const StyledFooterDialog = styled(DialogContent)`
  && {
    padding: 2em;
    ${media.phone`
    padding: 2em;`}
  }
`;
const StyledFooterGrid = styled(Grid)`
  && {
    background-color: #f0f0f0;
    border-radius: 6px;
    line-height: 1.7em;
    padding: 2rem 8rem;
    ${media.phone`
    padding:  0.5em 2em;`}
  }
`;
const StyledSubmitGrid = styled(Grid)`
  @media(max-width: 850px) {  margin: 2rem 0 0; }
 
  ${media.phone`margin: 0;`}
`;
const RightAlignGrid = styled(Grid)`
  text-align: right;
`;
const StyledDialogTitle = styled(DialogTitle)`
  && {
    padding: 0 24px;
    h2 {
      font-size: 2.4em;
      font-family: inherit;
      font-weight: bold;
      line-height: 1.167;
      margin-top: 40px;
      margin-bottom: 0;
    }
  }
`;
const FullWidthInput = styled(TextField)`
  &.MuiFormControl-root {
    width: 100%;
  }
`;

const OperatingTimes = styled.p`
  white-space: pre-wrap;
`

export default function EnquiryForm({ operatingTimes }: any) {
  const {
    app: { modal, phoneNumber },
  } = appSelector((state) => state);
  const dispatch = useDispatch();
  const router = useRouter();
  const [form, setForm] = useState({
    submitting: false,
    submitted: false,
    error: false,
    feedbackSubmitted: false,
    valid: false,
    privacyPolicy: 0,
    fields: [
      {
        label: `First name`,
        value: ``,
        error: false,
        fullWidth: false,
        type: "text",
      },
      {
        label: `Last name`,
        value: ``,
        error: false,
        fullWidth: false,
        type: "text",
      },
      {
        label: `Phone number`,
        value: ``,
        error: false,
        fullWidth: false,
        type: "tel",
      },
      {
        label: `Post code`,
        value: ``,
        error: false,
        fullWidth: false,
        type: "postCode",
      },
      {
        label: `Email address`,
        value: ``,
        error: false,
        fullWidth: true,
        type: "email",
      },
    ],
  });

  const recaptchaRef = createRef<ReCAPTCHA>();

  const onCaptchaChange = () => {
    recaptchaRef.current.getValue();
  };

  return (
    <Section>
        <StyledDialogTitle>
          <h2>Contact Us</h2>
        </StyledDialogTitle>
        <DialogContent id="modal-slide-description" style={{
          overflow: `hidden`,
        }}>
          {form.error ||
          (form.submitted && (
            <Typography variant="h3" component="h3" align="center">
              Thank you for contacting us.
              <br />
              <br />
              We are attending to your enquiry and will respond as soon as
              possible.
            </Typography>
          )) ||
          (form.submitting && (
            <>
              <Typography variant="h3" component="h3" align="center">
                Submitting
                <br />
                <br />
                <CircularProgress color="secondary" />
              </Typography>
            </>
          )) || (
            <>
              <p>
                Submit your contact details below and we will get back you in the
                next business day to see how we can help you maximise any
                compensation you may be entitled to.
              </p>
              <Form
                noValidate
                autoComplete="off"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (form.privacyPolicy < 2)
                    return setForm({
                      ...form,
                      privacyPolicy: 1,
                    });
                  handleSubmit(form, setForm, recaptchaRef);
                }}
              >
                {form.fields.map((field, id) =>
                  (
                    <TextField
                      label={field.label}
                      key={field.label}
                      variant="outlined"
                      value={field.value}
                      color="secondary"
                      helperText={field.error}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          fields: form.fields.map((f) =>
                            f.label === field.label
                              ? validateInput({ ...f, value: e.target.value })
                              : f
                          ),
                        })
                      }
                    />
                  )
                )}
                <StyledSubmitGrid container alignItems="center">
                  <Grid item xs={12} sm={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value="privacyPolicy"
                          checked={form.privacyPolicy === 2}
                          color="secondary"
                          onChange={() =>
                            setForm({
                              ...form,
                              privacyPolicy: form.privacyPolicy < 2 ? 2 : 1,
                            })
                          }
                        />
                      }
                      label={
                        <>
                          I agree to the{" "}
                          <a
                            href="/privacy-policy"
                            target="blank"
                            style={{ color: "#de4a22" }}
                          >
                            privacy policy
                          </a>
                        </>
                      }
                    />
                    {form.privacyPolicy === 1 && (
                      <FormHelperText>
                        Agree to the privacy policy to submit
                      </FormHelperText>
                    )}
                    <Grid container>
                    <ReCAPTCHA 
                      sitekey={process.env.RECAPTCHA_SITE_KEY}
                      onChange={() => onCaptchaChange()}
                      // size="invisible"
                      ref={recaptchaRef} 
                    />
                    </Grid>
                  </Grid>
                  <Grid container>
                    <StyledButton variant="outlined" type="submit" style={{marginTop: "1rem"}}>
                      Submit
                    </StyledButton>
                  </Grid>
                </StyledSubmitGrid>
              </Form>
            </>
          )}
        </DialogContent>
        <StyledFooterDialog>
          <StyledFooterGrid container alignItems="center">
            <Grid item sm={6}>
              <OperatingTimes dangerouslySetInnerHTML={{ __html: operatingTimes }} />
            </Grid>
            <Grid item sm={6}>
              <PhoneNumber>
                <StyledPhoneIcon />
                <a className="phone_number" href={`tel:${phoneNumber}`} onClick={() => {{
                  FbEvent('Contact');
                  Event({action: 'click', category: 'phone call tracking', event_label: 'Phone Call', event_action: 'Click to Call'})
                }}}>
                  {phoneNumber}
                </a>
              </PhoneNumber>
            </Grid>
          </StyledFooterGrid>
        </StyledFooterDialog>
    </Section>
  );
}
